import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';

// ── Utilities ─────────────────────────────────────────────────────────────────
const fmt = (dt) => dt ? new Date(dt).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';
const fmtSecs = (s) => { if (!s) return '—'; const m=Math.floor(s/60); const sec=s%60; return m>0?`${m}m ${sec}s`:`${sec}s`; };
const grade = (pct) => { if (pct===null||pct===undefined) return '—'; if(pct>=90)return'A+'; if(pct>=80)return'A'; if(pct>=70)return'B'; if(pct>=60)return'C'; if(pct>=50)return'D'; return'F'; };

const STATUS_COLOR = { submitted:'#16a34a', terminated:'#dc2626', in_progress:'#d97706' };
const GRADE_COLOR  = { 'A+':'#16a34a','A':'#22c55e','B':'#84cc16','C':'#eab308','D':'#f97316','F':'#dc2626','—':'#6b7280' };

// ── Format choice modal ───────────────────────────────────────────────────────
function FormatModal({ title, onPDF, onExcel, onClose }) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 380, textAlign: 'center' }}>
        <h3 className="modal-title" style={{ marginBottom: 8 }}>{title}</h3>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24 }}>
          Choose your preferred export format
        </p>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center', flexDirection: 'column', gap: 6, padding: '16px 12px' }}
            onClick={() => { onPDF(); onClose(); }}>
            <span style={{ fontSize: 28 }}>📄</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>PDF Report</span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>Print-ready</span>
          </button>
          <button className="btn btn-success" style={{ flex: 1, justifyContent: 'center', flexDirection: 'column', gap: 6, padding: '16px 12px' }}
            onClick={() => { onExcel(); onClose(); }}>
            <span style={{ fontSize: 28 }}>📊</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Excel (.xlsx)</span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>Spreadsheet</span>
          </button>
        </div>
        <button className="btn btn-secondary" style={{ marginTop: 14, width: '100%', justifyContent: 'center' }} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

// ── Load SheetJS dynamically ──────────────────────────────────────────────────
let xlsxReady = false;
const loadXlsx = () => new Promise((res) => {
  if (xlsxReady || window.XLSX) { xlsxReady = true; res(); return; }
  const s = document.createElement('script');
  s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
  s.onload = () => { xlsxReady = true; res(); };
  document.head.appendChild(s);
});

// ─── AttendanceReport ─────────────────────────────────────────────────────────
export default function AttendanceReport() {
  const { examId }  = useParams();
  const navigate    = useNavigate();
  const { user }    = useAuth();
  const printRef    = useRef();

  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [permError, setPermError] = useState(null);
  const [showFmt,   setShowFmt]   = useState(false);
  const [search,    setSearch]    = useState('');
  const [filter,    setFilter]    = useState('all'); // all | attempted | absent | terminated

  useEffect(() => {
    axios.get(`/api/exams/${examId}/attendance/full`)
      .then(r => setData(r.data))
      .catch(err => {
        const msg = err.response?.data?.error;
        if (err.response?.status === 403) {
          setLoading(false);
          setPermError(msg || 'Access denied');
        } else {
          toast.error('Failed to load data');
        }
      })
      .finally(() => setLoading(false));
  }, [examId]);

  if (permError) return (
    <div className="layout"><Sidebar />
      <main className="main-content">
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', textAlign:'center', padding:40 }}>
          <div style={{ fontSize:56, marginBottom:20 }}>🔒</div>
          <div style={{ fontWeight:700, fontSize:20, marginBottom:10, color:'var(--accent-warning)' }}>Access Restricted</div>
          <div style={{ fontSize:15, color:'var(--text-secondary)', maxWidth:420, lineHeight:1.8 }}>{permError}</div>
          <button className="btn btn-secondary" style={{ marginTop:24 }} onClick={() => window.history.back()}>← Go Back</button>
        </div>
      </main>
    </div>
  );

  if (loading) return (
    <div className="layout"><Sidebar />
      <main className="main-content">
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:400, gap:16 }}>
          <div className="spinner" style={{ width:44, height:44, borderWidth:4 }} />
          <div style={{ fontSize:14, color:'var(--text-secondary)' }}>Loading attendance data...</div>
        </div>
      </main>
    </div>
  );
  if (!data) return <div className="loading-screen"><p>Failed to load data</p></div>;

  const { exam, summary, students } = data;

  // Filter + search
  const filtered = students.filter(s => {
    const q = search.toLowerCase();
    const matchSearch = !q || s.full_name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q);
    const matchFilter =
      filter === 'all'        ? true :
      filter === 'attempted'  ? !!s.attempt_status :
      filter === 'absent'     ? !s.logged_in_at :
      filter === 'terminated' ? s.attempt_status === 'terminated' :
      filter === 'submitted'  ? s.attempt_status === 'submitted' : true;
    return matchSearch && matchFilter;
  });

  // ── PDF export via window.print() ─────────────────────────────────────────
  const exportPDF = () => {
    const printWindow = window.open('', '_blank');
    const html = buildPDFHTML(exam, summary, students);
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => { printWindow.focus(); printWindow.print(); };
  };

  // ── Excel export via SheetJS ───────────────────────────────────────────────
  const exportExcel = async () => {
    await loadXlsx();
    const XLSX = window.XLSX;

    // Sheet 1: Summary
    const summaryData = [
      ['Attendance Report', ''],
      ['Exam', exam.title],
      ['Organization', exam.org_name],
      ['Duration', `${exam.duration_minutes} minutes`],
      ['Total Marks', exam.total_marks],
      ['Passing Marks', `${exam.passing_marks} (${exam.passing_percentage}%)`],
      ['Generated On', new Date().toLocaleString()],
      [],
      ['SUMMARY', ''],
      ['Total Enrolled',  summary.total_enrolled],
      ['Logged In',       summary.logged_in],
      ['Attempted Exam',  summary.attempted],
      ['Submitted',       summary.submitted],
      ['Passed',          summary.passed],
      ['Failed',          summary.failed],
      ['Absent',          summary.absent],
      ['Average Score',   summary.avg_score],
      ['Average %',       `${summary.avg_percentage}%`],
    ];

    // Sheet 2: Student details
    const headers = [
      'Name', 'Email', 'Last Login', 'Instructions Opened',
      'Started At', 'Submitted At', 'Status', 'Sessions',
      'Time Spent', 'Score', 'Percentage', 'Grade', 'Passed',
      'Violations', 'Trust Score', 'Refresh Count', 'Terminated Reason', 'IP Address'
    ];

    const rows = students.map(s => [
      s.full_name, s.email,
      s.logged_in_at ? new Date(s.logged_in_at).toLocaleString() : 'Absent',
      s.instructions_opened_at ? new Date(s.instructions_opened_at).toLocaleString() : '—',
      s.started_at ? new Date(s.started_at).toLocaleString() : '—',
      s.submitted_at ? new Date(s.submitted_at).toLocaleString() : '—',
      s.attempt_status || 'Not Attempted',
      s.total_sessions,
      s.time_spent_seconds ? Math.round(s.time_spent_seconds/60) + ' min' : '—',
      s.score ?? '—',
      s.percentage ? `${parseFloat(s.percentage).toFixed(1)}%` : '—',
      grade(parseFloat(s.percentage)),
      s.passed === true ? 'Yes' : s.passed === false ? 'No' : '—',
      s.violations_count,
      s.trust_score ? `${parseFloat(s.trust_score).toFixed(1)}%` : '—',
      s.refresh_count || 0,
      s.termination_reason || '—',
      s.ip_address || '—',
    ]);

    const wb = XLSX.utils.book_new();

    // Summary sheet
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    wsSummary['!cols'] = [{ wch: 22 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

    // Detail sheet
    const wsDetail = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    wsDetail['!cols'] = [22,28,20,20,20,20,14,10,12,8,12,8,8,12,12,8,24,16].map(w=>({wch:w}));
    XLSX.utils.book_append_sheet(wb, wsDetail, 'Student Details');

    XLSX.writeFile(wb, `Attendance_${exam.title.replace(/\s+/g,'_')}_${new Date().toISOString().slice(0,10)}.xlsx`);
    toast.success('Excel file downloaded!');
  };

  // ── Stat card ─────────────────────────────────────────────────────────────
  const Stat = ({ label, val, color, sub }) => (
    <div style={{ padding: '16px 18px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)', textAlign: 'center' }}>
      <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{val}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color, marginTop: 2 }}>{sub}</div>}
    </div>
  );

  return (
    <div className="layout">
      <Sidebar />
      <main className="main-content">

        {/* Header */}
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 13, marginBottom: 6, padding: 0 }}>
              ← Back
            </button>
            <h1 className="page-title" style={{ margin: 0 }}>👥 Attendance Report</h1>
            <p className="page-subtitle" style={{ margin: 0 }}>{exam.title} · {exam.org_name}</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={() => setShowFmt(true)}>
              ⬇ Export
            </button>
          </div>
        </div>

        {/* Exam Details */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, fontSize: 13 }}>
            {[
              { label: 'Duration',      val: `${exam.duration_minutes} min` },
              { label: 'Total Marks',   val: exam.total_marks },
              { label: 'Passing Marks', val: `${exam.passing_marks} (${exam.passing_percentage}%)` },
              { label: 'Proctored',     val: exam.proctoring_enabled ? 'Yes' : 'No' },
              { label: 'Status',        val: exam.is_published ? 'Published' : 'Draft' },
            ].map(f => (
              <div key={f.label} style={{ padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: 8 }}>
                <div style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 3 }}>{f.label}</div>
                <div style={{ fontWeight: 600 }}>{f.val}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 12, marginBottom: 20 }}>
          <Stat label="Enrolled"  val={summary.total_enrolled} color="var(--accent-primary)" />
          <Stat label="Logged In" val={summary.logged_in}      color="var(--accent-secondary)" />
          <Stat label="Attempted" val={summary.attempted}      color="var(--accent-warning)" />
          <Stat label="Submitted" val={summary.submitted}      color="var(--accent-primary)" />
          <Stat label="Passed"    val={summary.passed}         color="var(--accent-success)"  sub={summary.submitted > 0 ? `${Math.round(summary.passed/summary.submitted*100)}%` : ''} />
          <Stat label="Failed"    val={summary.failed}         color="var(--accent-danger)" />
          <Stat label="Absent"    val={summary.absent}         color="var(--text-muted)" />
          <Stat label="Avg Score" val={summary.avg_score}      color="var(--accent-secondary)" sub={summary.avg_percentage ? `${summary.avg_percentage}%` : ''} />
        </div>

        {/* Filter bar */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <input className="form-input" style={{ width: 220, padding: '7px 12px' }}
            placeholder="🔍 Search name or email..." value={search} onChange={e => setSearch(e.target.value)} />
          {[
            ['all','All'],['attempted','Attempted'],['submitted','Submitted'],
            ['terminated','Terminated'],['absent','Absent'],
          ].map(([val, lbl]) => (
            <button key={val} onClick={() => setFilter(val)} className="btn btn-secondary btn-sm"
              style={filter===val ? { borderColor:'var(--accent-primary)', color:'var(--accent-primary)', background:'rgba(79,142,247,0.1)' } : {}}>
              {lbl} {val==='all' ? `(${students.length})` : `(${students.filter(s =>
                val==='attempted'?!!s.attempt_status:val==='absent'?!s.logged_in_at:s.attempt_status===val
              ).length})`}
            </button>
          ))}
        </div>

        {/* Student table */}
        <div className="card" style={{ padding: 0 }}>
          <div className="table-container" style={{ border: 'none' }}>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Student</th>
                  <th>Last Login</th>
                  <th>Status</th>
                  <th>Sessions</th>
                  <th>Time Spent</th>
                  <th>Score</th>
                  <th>Grade</th>
                  <th>IP Address</th>
                  <th>Violations</th>
                  <th>Trust</th>
                  <th>Refreshes</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={12} style={{ textAlign:'center', padding:32, color:'var(--text-secondary)' }}>No students found</td></tr>
                )}
                {filtered.map((s, i) => {
                  const pct = parseFloat(s.percentage);
                  const g   = grade(pct);
                  return (
                    <tr key={s.id}>
                      <td style={{ color:'var(--text-muted)', fontSize:12 }}>{i+1}</td>
                      <td>
                        <div style={{ fontWeight:600, fontSize:13 }}>{s.full_name}</div>
                        <div style={{ fontSize:11, color:'var(--text-muted)' }}>{s.email}</div>
                      </td>
                      <td style={{ fontSize:12 }}>
                        {s.logged_in_at
                          ? <span style={{ color:'var(--accent-success)' }}>{fmt(s.logged_in_at)}</span>
                          : <span style={{ color:'var(--accent-danger)' }}>Absent</span>}
                      </td>
                      <td>
                        {s.attempt_status
                          ? <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:600, background:`${STATUS_COLOR[s.attempt_status] || '#6b7280'}22`, color:STATUS_COLOR[s.attempt_status] || '#6b7280' }}>{s.attempt_status}</span>
                          : <span style={{ color:'var(--text-muted)', fontSize:12 }}>—</span>}
                      </td>
                      <td style={{ textAlign:'center', fontWeight:600 }}>{s.total_sessions || '—'}</td>
                      <td style={{ fontSize:12 }}>{fmtSecs(s.time_spent_seconds)}</td>
                      <td style={{ fontWeight:600 }}>
                        {s.score != null ? <>{s.score}<span style={{ color:'var(--text-muted)', fontSize:11 }}>/{exam.total_marks}</span></> : '—'}
                      </td>
                      <td>
                        {g !== '—'
                          ? <span style={{ fontWeight:800, fontSize:15, color:GRADE_COLOR[g] }}>{g}</span>
                          : <span style={{ color:'var(--text-muted)' }}>—</span>}
                      </td>
                      <td style={{ fontSize:11, fontFamily:'var(--font-mono)', color:'var(--text-muted)' }}>{s.ip_address||'—'}</td>
                      <td style={{ textAlign:'center' }}>
                        {s.violations_count > 0
                          ? <span style={{ color:'var(--accent-danger)', fontWeight:600 }}>{s.violations_count}</span>
                          : <span style={{ color:'var(--text-muted)' }}>0</span>}
                      </td>
                      <td style={{ fontSize:12 }}>
                        <span style={{ color: s.trust_score < 60 ? 'var(--accent-danger)' : s.trust_score < 80 ? 'var(--accent-warning)' : 'var(--accent-success)' }}>
                          {s.trust_score ? `${parseFloat(s.trust_score).toFixed(0)}%` : '—'}
                        </span>
                      </td>
                      <td style={{ textAlign:'center', fontSize:12 }}>
                        {s.refresh_count > 0 ? <span style={{ color:'var(--accent-danger)' }}>{s.refresh_count}</span> : '0'}
                      </td>
                      <td>
                        {s.attempt_id && (
                          <button className="btn btn-secondary btn-sm" style={{ fontSize:11 }}
                            onClick={() => navigate(`/reports/result/${examId}/${s.attempt_id}`)}>
                            📋 Result
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

      </main>

      {showFmt && (
        <FormatModal
          title="Export Attendance Report"
          onPDF={exportPDF}
          onExcel={exportExcel}
          onClose={() => setShowFmt(false)}
        />
      )}
    </div>
  );
}

// ── PDF HTML builder ──────────────────────────────────────────────────────────
function buildPDFHTML(exam, summary, students) {
  const rows = students.map((s, i) => {
    const pct = parseFloat(s.percentage);
    const g   = grade(pct);
    return `
      <tr>
        <td>${i+1}</td>
        <td><strong>${s.full_name}</strong><br/><small>${s.email}</small></td>
        <td>${s.logged_in_at ? new Date(s.logged_in_at).toLocaleString() : '<span style="color:#dc2626">Absent</span>'}</td>
        <td><span style="padding:2px 7px;border-radius:12px;font-size:11px;font-weight:700;background:${(STATUS_COLOR[s.attempt_status]||'#6b7280')}22;color:${STATUS_COLOR[s.attempt_status]||'#6b7280'}">${s.attempt_status||'—'}</span></td>
        <td style="text-align:center">${s.total_sessions||'—'}</td>
        <td>${fmtSecs(s.time_spent_seconds)}</td>
        <td style="text-align:center;font-weight:700">${s.score != null ? `${s.score}/${exam.total_marks}` : '—'}</td>
        <td style="text-align:center;font-weight:800;font-size:15px;color:${GRADE_COLOR[g]||'#6b7280'}">${g}</td>
        <td style="text-align:center;color:${s.violations_count>0?'#dc2626':'#6b7280'};font-weight:${s.violations_count>0?700:400}">${s.violations_count}</td>
        <td style="text-align:center">${s.trust_score ? parseFloat(s.trust_score).toFixed(0)+'%' : '—'}</td>
        <td style="text-align:center;color:${s.refresh_count>0?'#dc2626':'inherit'}">${s.refresh_count||0}</td>
      </tr>`;
  }).join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Attendance Report — ${exam.title}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:Arial,sans-serif;font-size:12px;color:#111;padding:20px}
    h1{font-size:20px;margin-bottom:4px}
    .subtitle{color:#555;font-size:13px;margin-bottom:20px}
    .meta{display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin-bottom:20px}
    .meta-box{border:1px solid #ddd;border-radius:6px;padding:8px 10px}
    .meta-label{font-size:10px;color:#888;margin-bottom:2px}
    .meta-value{font-weight:700;font-size:13px}
    .summary{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:20px}
    .stat{border:1px solid #ddd;border-radius:6px;padding:10px;text-align:center}
    .stat-val{font-size:22px;font-weight:800}
    .stat-lbl{font-size:10px;color:#888;margin-top:2px}
    table{width:100%;border-collapse:collapse;font-size:11px}
    th{background:#1e293b;color:#fff;padding:7px 8px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.5px}
    td{padding:6px 8px;border-bottom:1px solid #e5e7eb;vertical-align:middle}
    tr:nth-child(even){background:#f9fafb}
    .footer{margin-top:16px;font-size:10px;color:#888;text-align:right}
    @media print{@page{margin:15mm}body{padding:0}}
  </style>
</head>
<body>
  <h1>Attendance Report</h1>
  <div class="subtitle">${exam.title} · ${exam.org_name} · Generated ${new Date().toLocaleString()}</div>

  <div class="meta">
    <div class="meta-box"><div class="meta-label">Duration</div><div class="meta-value">${exam.duration_minutes} min</div></div>
    <div class="meta-box"><div class="meta-label">Total Marks</div><div class="meta-value">${exam.total_marks}</div></div>
    <div class="meta-box"><div class="meta-label">Passing Marks</div><div class="meta-value">${exam.passing_marks} (${exam.passing_percentage}%)</div></div>
    <div class="meta-box"><div class="meta-label">Proctored</div><div class="meta-value">${exam.proctoring_enabled?'Yes':'No'}</div></div>
    <div class="meta-box"><div class="meta-label">Max Warnings</div><div class="meta-value">${exam.max_warnings}</div></div>
  </div>

  <div class="summary">
    <div class="stat"><div class="stat-val" style="color:#3b82f6">${summary.total_enrolled}</div><div class="stat-lbl">Enrolled</div></div>
    <div class="stat"><div class="stat-val" style="color:#8b5cf6">${summary.logged_in}</div><div class="stat-lbl">Logged In</div></div>
    <div class="stat"><div class="stat-val" style="color:#f59e0b">${summary.attempted}</div><div class="stat-lbl">Attempted</div></div>
    <div class="stat"><div class="stat-val" style="color:#3b82f6">${summary.submitted}</div><div class="stat-lbl">Submitted</div></div>
    <div class="stat"><div class="stat-val" style="color:#16a34a">${summary.passed}</div><div class="stat-lbl">Passed</div></div>
    <div class="stat"><div class="stat-val" style="color:#dc2626">${summary.failed}</div><div class="stat-lbl">Failed</div></div>
    <div class="stat"><div class="stat-val" style="color:#6b7280">${summary.absent}</div><div class="stat-lbl">Absent</div></div>
    <div class="stat"><div class="stat-val" style="color:#8b5cf6">${summary.avg_score}</div><div class="stat-lbl">Avg Score (${summary.avg_percentage}%)</div></div>
  </div>

  <table>
    <thead><tr>
      <th>#</th><th>Student</th><th>Last Login</th><th>Status</th>
      <th>Sessions</th><th>Time Spent</th><th>Score</th><th>Grade</th>
      <th>Violations</th><th>Trust</th><th>Refreshes</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="footer">ExamAI Attendance Report · ${exam.title} · ${new Date().toLocaleDateString()}</div>
</body>
</html>`;
}
